import { useEffect } from "react";
import { FieldData } from "../types";
import { PiiCompletion } from "src/api/piiCompletion";

export interface UseFormPersistenceArgs {
    fieldsData: { [k: string]: FieldData };
    dynamicThankYouFields: string[] | undefined;
    isSecondServiceForm: boolean | undefined;
    isPiiCompletionForm: boolean | undefined;
    piiCompletionData: PiiCompletion | null | undefined;
    successMessage: unknown;
    formId: number | string;
    isFormInsideOffersModal: boolean;
    reloadFormData: (() => void) | undefined;
}

/**
 * Manages form data persistence:
 * - Mirrors form state into `localStorage` (dynamic fields, global form values, success message).
 * - Listens for `formDataUpdated` window events to sync state between
 *   modal and page form instances.
 */
export function useFormPersistence({
    fieldsData,
    dynamicThankYouFields,
    isSecondServiceForm,
    isPiiCompletionForm,
    piiCompletionData,
    successMessage,
    formId,
    isFormInsideOffersModal,
    reloadFormData,
}: UseFormPersistenceArgs): void {
    // Storage sync
    useEffect(() => {
        const params: { [x: string]: string } = {};
        if (dynamicThankYouFields && !isSecondServiceForm) {
            dynamicThankYouFields.forEach((field) => {
                params[field] = fieldsData[field]?.value;
            });
            localStorage.setItem("dynamicFields", JSON.stringify(params));
        }
        if (isPiiCompletionForm) {
            piiCompletionData?.fields.forEach((field) => {
                params[field.codeName] = fieldsData[field.codeName]?.value;
            });
            localStorage.setItem("dynamicFields", JSON.stringify(params));
        }

        if (!isSecondServiceForm) {
            const formAnswers = Object.keys(fieldsData).reduce((res, key) => {
                res = {
                    ...res,
                    [key]: fieldsData[key].value,
                };
                return res;
            }, {});
            // @ts-ignore
            window.formAnswers = formAnswers;
            localStorage.setItem(
                "globalFormValues",
                JSON.stringify(formAnswers),
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fieldsData]);

    useEffect(() => {
        if (!isSecondServiceForm) {
            localStorage.setItem(
                "successMessage",
                JSON.stringify(successMessage),
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Cross-instance data update listener
    useEffect(() => {
        const handleStorageUpdate = (event: CustomEvent) => {
            const detail = event.detail as { formId?: string } | undefined;
            if (
                detail?.formId === String(formId) &&
                !isFormInsideOffersModal &&
                reloadFormData &&
                typeof reloadFormData === "function"
            ) {
                (reloadFormData as () => void)();
            }
        };

        window.addEventListener(
            "formDataUpdated",
            handleStorageUpdate as EventListener,
        );

        return () => {
            window.removeEventListener(
                "formDataUpdated",
                handleStorageUpdate as EventListener,
            );
        };
    }, [formId, isFormInsideOffersModal, reloadFormData]);
}
